import React, { useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigateWithSearchContext } from '../../hooks/useNavigateWithSearchContext';
import MobileAccountCard from '../Account/MobileAccountCard';
import LanguageToggle from '../Chrome/LanguageToggle';
import './MobileLayout.css';

interface MobileLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  isMapPage?: boolean; // Indicates if this is the main map page (locks scrolling)
}

interface MenuSection {
  title: string;
  items: {
    id: string;
    icon: string;
    label: string;
    path?: string;
    action?: () => void;
    requiresAuth?: boolean;
    highlight?: boolean;
  }[];
}

const MobileLayout: React.FC<MobileLayoutProps> = ({ 
  children, 
  showHeader = true,
  isMapPage = false
}) => {
  const navigate = useNavigate();
  const navigateWithContext = useNavigateWithSearchContext();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Swipe functionality state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Slide-out menu sections
  const menuSections: MenuSection[] = [
    {
      title: t.common.navigation,
      items: [
        { id: 'home', icon: '', label: t.common.home, path: '/' },
        { id: 'permitreview', icon: '', label: t.common.permitReview, path: '/permitreview' },
        // Add pricing page only for non-authenticated users
        ...(user ? [] : [{ id: 'pricing', icon: '', label: t.common.pricing, path: '/pricing' }]),
        { id: 'legal', icon: '', label: t.common.legal, path: '/legal' },
        { id: 'contact', icon: '', label: t.common.contact, path: '/contact' },
      ],
    },
    {
      title: t.common.account,
      items: user ? [
        { id: 'overview', icon: '', label: t.common.overview, path: '/account/overview' },
        { id: 'settings', icon: '', label: t.common.settings, path: '/account/settings' },
        { id: 'reports', icon: '', label: t.common.reports, path: '/account/reports' },
        { id: 'pricing', icon: '', label: t.common.plansPricing, path: '/account/pricing' },
      ] : [],
    },
  ];

  // Add logout to account section if user is logged in
  if (user) {
    menuSections[1].items.push({
      id: 'logout',
      icon: '',
      label: t.common.logOut,
      action: handleLogout,
    });
  }

  // Smooth menu close with animation
  const closeMenuWithAnimation = () => {
    if (isMenuClosing) return; // Prevent multiple close attempts
    
    setIsMenuClosing(true);
    
    // After animation completes, actually close the menu
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
    }, 300); // Match CSS animation duration
  };

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await signOut();
      closeMenuWithAnimation();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  }



  const handleMenuItemClick = (item: MenuSection['items'][0]) => {
    if (item.action) {
      item.action();
      return;
    }

    if (item.path) {
      // Use context-aware navigation for pricing pages
      const isPricingPath = item.path.includes('/pricing');
      if (isPricingPath) {
        navigateWithContext(item.path);
      } else {
        navigate(item.path);
      }
      closeMenuWithAnimation();
    }
  };

  const isActiveRoute = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Swipe functionality
  const minSwipeDistance = 80; // Minimum distance for a swipe
  const maxVerticalDistance = 100; // Maximum vertical movement allowed for horizontal swipe

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || !touchEnd) return;
    
    const horizontalDistance = touchStart - touchEnd;
    const verticalDistance = Math.abs(
      e.changedTouches[0].clientY - (e.target as any).startY || 0
    );
    
    const isLeftSwipe = horizontalDistance > minSwipeDistance;
    const isHorizontalSwipe = verticalDistance < maxVerticalDistance;

    // Close menu on left swipe (swipe to the left) only if it's primarily horizontal
    if (isLeftSwipe && isHorizontalSwipe) {
      closeMenuWithAnimation();
    }
    
    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Enhanced touch start to track Y position too
  const onTouchStartEnhanced = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    // Store Y position for vertical movement detection
    (e.target as any).startY = e.targetTouches[0].clientY;
  };

  return (
    <div className="mobile-layout">
      {/* Mobile Header */}
      {showHeader && (
        <header className="mobile-header">
          <div className="mobile-header-content">
            <button 
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg viewBox="0 0 24 24" className="mobile-menu-icon">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="none"/>
                <rect x="3" y="6" width="18" height="2" rx="1"/>
                <rect x="3" y="11" width="18" height="2" rx="1"/>
                <rect x="3" y="16" width="18" height="2" rx="1"/>
              </svg>
            </button>
            
            <div className="mobile-brand">
              {/* Logo removed for cleaner mobile experience */}
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LanguageToggle compact />
              <button 
                className="mobile-auth-btn"
                onClick={() => navigate(user ? '/account' : '/login')}
              >
                {user ? t.common.account : t.common.signIn}
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={`mobile-main ${isMapPage ? 'map-page' : ''}`}>
        {children}
      </main>



      {/* Slide-out Menu */}
      {isMenuOpen && (
        <>
          <div 
            className={`mobile-menu-backdrop ${isMenuClosing ? 'closing' : ''}`}
            onClick={closeMenuWithAnimation}
          />
          <div 
            className={`mobile-menu ${isMenuClosing ? 'closing' : ''}`}
            onTouchStart={onTouchStartEnhanced}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="mobile-menu-header">
              <button 
                className="mobile-menu-close"
                onClick={closeMenuWithAnimation}
              >
                ×
              </button>
            </div>
            
            {/* Mobile Account Card */}
            <MobileAccountCard compact={true} />

            <div className="mobile-menu-content">
              {menuSections.map((section) => 
                // Only show sections that have items
                section.items.length > 0 ? (
                  <div key={section.title} className="mobile-menu-section">
                    <h3 className="mobile-menu-section-title">{section.title}</h3>
                    <div className="mobile-menu-items">
                      {section.items.map((item) => (
                        <button
                          key={item.id}
                          className={`mobile-menu-item ${
                            item.highlight ? 'highlight' : ''
                          } ${
                            item.path && isActiveRoute(item.path) ? 'active' : ''
                          }`}
                          onClick={() => handleMenuItemClick(item)}
                          disabled={item.id === 'logout' && isLoggingOut}
                        >
                          <span className="mobile-menu-item-icon">{item.icon}</span>
                          <span className="mobile-menu-item-label">
                            {item.id === 'logout' && isLoggingOut ? 'Logging out...' : item.label}
                          </span>
                          <span className="mobile-menu-item-arrow">→</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileLayout; 